import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { UserService } from '../../api/generated/services/UserService';
import { UserAvvService } from '../../api/generated/services/UserAvvService';
import type { UserView } from '../../api/generated/models/UserView';
import type { UserUpdateRequest } from '../../api/generated/models/UserUpdateRequest';
import { startLoading, stopLoading } from './loadingSlice';
import type { RootState } from '../store';

interface ProfileState {
  contact?: UserView;
  avv: {
    isAccepted: boolean;
    acceptedDate?: string;
  };
  loading: boolean;
  error?: string;
}

const initialState: ProfileState = {
  avv: {
    isAccepted: false
  },
  loading: false
};

export const fetchProfile = createAsyncThunk<
  { contact: UserView; avv: { avvAcceptedDate?: string | null } },
  void,
  { state: RootState }
>(
  'profile/fetch',
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      const [profileResponse, avvResponse] = await Promise.all([
        UserService.getAppUserGet(),
        UserAvvService.getAppUserAvvAcceptedDate()
      ]);

      return {
        contact: profileResponse,
        avv: avvResponse
      };
    } finally {
      dispatch(stopLoading());
    }
  },
  {
    // Drop a duplicate dispatch while a request is already in flight. The
    // bootstrap, ProfilePage save flows and reset-after-update can otherwise
    // race; this keeps the network tab clean without changing semantics.
    condition: (_, { getState }) => !getState().profile.loading,
  }
);

export const acceptAvv = createAsyncThunk(
  'profile/acceptAvv',
  async (_, { dispatch }) => {
    dispatch(startLoading());
    try {
      await UserAvvService.postAppUserAvvAccept();
      return new Date().toISOString();
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const updateContact = createAsyncThunk(
  'profile/updateContact',
  async (contact: UserUpdateRequest, { dispatch }) => {
    dispatch(startLoading());
    try {
      await UserService.putAppUserUpdate(contact);
      // Fetch updated profile
      const updatedProfile = await UserService.getAppUserGet();
      return updatedProfile;
    } finally {
      dispatch(stopLoading());
    }
  }
);

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    setProfile: (state, action: PayloadAction<ProfileState>) => {
      return { ...state, ...action.payload };
    },
    updateContactData: (state, action: PayloadAction<Partial<UserView>>) => {
      if (state.contact) {
        state.contact = {
          ...state.contact,
          ...action.payload
        };
      } else {
        // Set contact data if it doesn't exist yet (e.g., after login)
        state.contact = action.payload as UserView;
      }
      
      // Update AVV status if avvAcceptedDate is present in payload
      if (action.payload.avvAcceptedDate !== undefined) {
        state.avv = {
          isAccepted: !!action.payload.avvAcceptedDate,
          acceptedDate: action.payload.avvAcceptedDate || undefined
        };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.contact = action.payload.contact;
        state.avv = {
          isAccepted: !!action.payload.avv?.avvAcceptedDate,
          acceptedDate: action.payload.avv?.avvAcceptedDate || undefined
        };
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      .addCase(acceptAvv.fulfilled, (state, action) => {
        state.avv.isAccepted = true;
        state.avv.acceptedDate = action.payload;
      })
      .addCase(updateContact.fulfilled, (state, action) => {
        state.contact = action.payload;
      });
  }
});

export const { setProfile, updateContactData } = profileSlice.actions;
export default profileSlice.reducer;
